import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './HomePage.css';

/* ─────────────────────────────────────────────────────────────────────────────
   INLINE SVG ICONS  (zero icon-library dependency)
   ───────────────────────────────────────────────────────────────────────────── */
const IconArrow = () => (
  <svg className="stx-arrow" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M1 7h12M7 1l6 6-6 6" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconSun = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="12" r="5"/>
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IconMoon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/>
  </svg>
);

const IconWhatsApp = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/* ─────────────────────────────────────────────────────────────────────────────
   DATA
   ───────────────────────────────────────────────────────────────────────────── */
const PROGRAMS = [
  {
    emoji:    '🏫',
    tag:      'Schools',
    title:    'School Robotics Programs',
    desc:     'End-to-end robotics curriculum delivered directly in your school — from lesson plans and teacher training to national competition prep.',
    features: [
      'Aligned to Kenya CBC & STEM objectives',
      'Trained robotics coaches on-site every week',
      'Progress tracking & termly reports for parents',
    ],
    cta:  'Book a Free School Demo',
    href: '/join-us',
  },
  {
    emoji:    '☀️',
    tag:      'Holiday Camps',
    title:    'Holiday Robotics Camps',
    desc:     'Intensive 1–4 week camps where students build, code, and compete — perfect for April, August, and December holidays.',
    features: [
      'Ages 4–17, all skill levels welcome',
      'Design, Build and program different robots',
      'Fun, hands-on projects with real-world themes',
    ],
    cta:  'View Upcoming Camps',
    href: '/join-us',
  },
  {
    emoji:    '🏆',
    tag:      'Competitions',
    title:    'Robotics Competitions',
    desc:     'Prepare your team for the Inspire Robotics Challenge, RoboFest, and international Robotics tournaments.',
    features: [
      'National and international competition stages',
      'Expert coaching, strategy & mock tournaments',
      'Top teams travel and compete globally',
    ],
    cta:  'Enter a Competition',
    href: '/join-us',
  },
  {
    emoji:    '📦',
    tag:      'Robotics Kits',
    title:    'Educational Robotics Kits',
    desc:     'Premium, age-graded kits for classroom or home use. Each kit includes curriculum guides, all parts, and  coach support.',
    features: [
      'Beginner (4–9), Intermediate (10–13), Advanced (14+)',
      'Comprehensive teacher & parent activity guides',
      
    ],
    cta:  'Shop Robotics Kits',
    href: '/products',
  },
];

const WHY_ITEMS = [
  { icon: '🛠️', title: 'Hands-On, Project-Based Learning',    desc: 'Students build real robots from day one. No passive lectures — pure creation, experimentation, and problem-solving.' },
  { icon: '🌍', title: 'International Exposure',               desc: 'Top teams compete in China, the USA, and across Africa — giving students a global network and a world-class stage.' },
  { icon: '📋', title: 'CBC & STEM Curriculum Aligned',        desc: "Programs integrate seamlessly with Kenya's Competency-Based Curriculum, making adoption easy for any school." },
  { icon: '🏅', title: 'Proven, Trackable Results',            desc: 'Parents and principals receive regular progress reports — real growth in skills, confidence, and competition results.' },
];

const TESTIMONIALS = [
  {
    stars:   '★★★★★',
    quote:   'Before Stemtrix, robotics felt like something from another world. Now our students are designing robots that solve real community problems — and one team is heading to Beijing. The transformation has been extraordinary.',
    result:  '🏆 Team qualified for Beijing 2025',
    // name:    'David Gideon Kariuki HSC',
    // role:    'Principal, Merishaw School',
    initials:'DG',
  },
  {
    stars:   '★★★★★',
    quote:   "My daughter used to shy away from science. Six months into the Stemtrix program, she's leading her school's robotics team and telling me she wants to be an aerospace engineer. I genuinely did not expect this.",
    result:  '📈 From reluctant learner to team captain',
    name:    'Sarah Kimani',
    role:    'Parent, Nairobi',
    initials:'SK',
  },
  {
    stars:   '★★★★★',
    quote:   "The curriculum is thoughtfully designed — it grows with the students. As a robotics coach I've used many platforms, and Stemtrix strikes the best balance between rigour and fun. My students never want the session to end.",
    result:  '🎯 3 of 5 students advanced to nationals',
    name:    'Isaac Arabu',
    role:    'Robotics Coach & Educator',
    initials:'IA',
  },
];

const TRUST_STATS = [
  { num: '20+',  lbl: 'Partner schools across Kenya' },
  { num: '500+', lbl: 'Students trained in robotics & coding' },
  { num: '3',    lbl: 'International competitions entered' },
  { num: '5+',   lbl: "Years building Kenya's future innovators" },
];

const PARTNERS = [

];

const HERO_BADGES = [
  { icon: '🌍', title: 'International',     sub: 'Beijing & Michigan' },
  { icon: '🏆', title: 'Competition Ready', sub: 'Inspire & RoboFest'  },
  { icon: '📚', title: 'CBC Aligned',       sub: 'Curriculum approved' },
  { icon: '🤖', title: 'Hands-On Kits',     sub: 'Ages 4–18'           },
];

const NEWS_ITEMS = [
  {
  iframe: 'https://www.youtube.com/embed/T-PeDh3SCAo?start=169',
  date: 'March 6,7, 2026',
  title: 'Inspire Robotics Challenge 2026',
  excerpt: 'Schools competed at St, Andrews school, Turi under the theme "Climate." Winners will represent Kenya in Beijing and Austria.',
  href: '/news/inspire-robotics-challenge-2026'
},
  { iframe: 'https://www.youtube.com/embed/eV7RRlNloZs', date: 'March 15, 2025',   title: 'Inspire Robotics Challenge 2025',     excerpt: 'Schools competed at Potterhouse under the theme "Health." Winners represent Kenya in Beijing.',  href: '/news/inspire-robotics-challenge-2025'  },
  { iframe: 'https://www.youtube.com/embed/8P7GzmF8Xf0', date: 'April 5, 2025',    title: 'RoboFest at Merishaw School',          excerpt: 'Seven schools clashed in robotics challenges, with top teams earning global spots in Michigan.', href: '/news/robofest-merishaw-school'          },
  { iframe: 'https://www.youtube.com/embed/cfNnN_UxdpQ', date: 'February 10, 2025',title: 'Inspire Robotics Champions 2023/24',   excerpt: 'Riara Springs Girls claimed the championship with outstanding innovation and teamwork.',        href: '/news/inspire-robotics-champions-2024'  },
  { iframe: 'https://www.youtube.com/embed/RKgyaGQUqsE', date: 'January 15, 2025', title: '2023–2024 Robotics Season Highlights', excerpt: "A season of breakthroughs — the best moments from Kenya's top student robotics teams.",         href: '/news/robotics-season-2023-2024'        },
];

const CTA_CARDS = [
  { icon: '🏫', title: 'For Schools & Principals', desc: 'Free 30-minute demo of our full school robotics program, curriculum, and pricing.' },
  { icon: '👨‍👩‍👧', title: 'For Parents',               desc: 'Enroll your child in after-school robotics or an upcoming holiday camp.' },
  { icon: '🤝', title: 'For Partners & Sponsors',   desc: 'Support STEM innovation in Kenya — explore partnership and sponsorship opportunities.' },
];

/* ─────────────────────────────────────────────────────────────────────────────
   THEME TOGGLE COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
const ThemeToggle = ({ isDark, onToggle }) => (
  <button
    className="stx-theme-toggle"
    onClick={onToggle}
    aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    title={isDark ? 'Light mode' : 'Dark mode'}
  >
    <div className="stx-toggle-track">
      <div className="stx-toggle-knob">
        {isDark ? <IconMoon /> : <IconSun />}
      </div>
    </div>
    <span className="stx-toggle-label">
      {isDark ? 'Dark' : 'Light'}
    </span>
  </button>
);

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
const HomePage = () => {
  /* Theme — persisted to localStorage */
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('stx-theme');
      if (saved) return saved === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch { return false; }
  });

  /* Scroll-driven states */
  const [navScrolled, setNavScrolled] = useState(false);
  const [waVisible,   setWaVisible]   = useState(false);

  /* Apply theme attribute to <html> */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    try { localStorage.setItem('stx-theme', isDark ? 'dark' : 'light'); } catch {}
  }, [isDark]);

  /* Scroll listener */
  useEffect(() => {
    const onScroll = () => {
      setNavScrolled(window.scrollY > 60);
      setWaVisible(window.scrollY > 220);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleTheme = useCallback(() => setIsDark(d => !d), []);

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <>
      <Helmet>
        <title>Stemtrix | #1 Robotics & STEM Education in Kenya — Build. Code. Compete.</title>
        <meta name="description"
          content="Kenya's leading robotics education company. School programs, holiday camps, international competitions & premium robotics kits for ages 4–18. Enroll today." />
        <meta name="keywords"
          content="robotics training Kenya, STEM education Kenya, coding for kids Kenya, robotics for schools Nairobi, robotics kits Kenya, CBC STEM program Kenya, holiday robotics camps Kenya" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="stx-page">

        {/* ══════════════════════════════════════════════════════════════════
            NAVBAR
            ══════════════════════════════════════════════════════════════════ */}
        <nav className={`stx-nav ${navScrolled ? 'scrolled' : ''}`} role="navigation" aria-label="Main navigation">
          <Link to="/" className="stx-nav-logo" aria-label="Stemtrix home">
            STEM<span>TRIX</span>
          </Link>

          <ul className="stx-nav-links">
            <li><Link to="/programs">Programs</Link></li>
            <li><Link to="/products">Kits</Link></li>
            <li><Link to="/news">News</Link></li>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>

          <div className="stx-nav-right">
            {/* ── THEME TOGGLE ── */}
            <ThemeToggle isDark={isDark} onToggle={toggleTheme} />

            <Link
              to="/join-us"
              className="stx-btn stx-btn-primary"
              style={{ padding: '10px 22px', fontSize: 14 }}
            >
              Enroll Now <IconArrow />
            </Link>
          </div>
        </nav>

        {/* ══════════════════════════════════════════════════════════════════
            HERO
            ══════════════════════════════════════════════════════════════════ */}
        <section className="stx-hero" aria-label="Hero">
          {/* Decorative background elements */}
          <div className="stx-hero-deco" aria-hidden="true">
            <div className="stx-hero-deco-circle c1" />
            <div className="stx-hero-deco-circle c2" />
            <div className="stx-hero-deco-line" />
          </div>
          <div className="stx-hero-dots" aria-hidden="true" />

          <div className="stx-container">
            <div className="stx-hero-inner">

              {/* Left — copy */}
              <div>
                <div className="stx-hero-eyebrow">Kenya's #1 Robotics Education Company</div>

                <h1 className="stx-hero-h1">
                  Turn Your Child Into
                  <span className="stx-hero-h1-accent"> a Future Innovator</span>
                </h1>

                <p className="stx-hero-sub">
                  World-class robotics training, coding programs, and international
                  competitions — right here in Kenya. We build the skills, confidence,
                  and creativity that tomorrow's engineers need.
                </p>

                <div className="stx-urgency" role="alert">
                  <a href="https://docs.google.com/forms/d/1Qcd3Vo2bHRAFNsuh6CCX3jonSDIZR2FBluw_NJ0ZA7M/edit" className="stx-urgency-link">
                  <span className="stx-urgency-dot" aria-hidden="true" />
                  RC.CODE Challenge 2026 is now open for registration!
                  </a>
                </div>

                <div className="stx-hero-ctas">
                  <Link to="/join-us" className="stx-btn stx-btn-primary">
                    Join our STEM Learning Program <IconArrow />
                  </Link>
                  <Link to="/contact" className="stx-btn stx-btn-outline">
                    Book a Free School Demo
                  </Link>
                </div>

                {/* Stats */}
                <div className="stx-hero-stats" aria-label="Key statistics">
                  {[
                    { num: '20+',  lbl: 'Partner Schools' },
                    { num: '500+', lbl: 'Students Trained' },
                    { num: '3',    lbl: 'Int\'l Competitions' },
                  ].map((s, i) => (
                    <div className="stx-hero-stat" key={i}>
                      <span className="stx-hero-stat-num">{s.num}</span>
                      <span className="stx-hero-stat-lbl">{s.lbl}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — visual panel */}
              <div className="stx-hero-visual" aria-hidden="true">
                <div className="stx-hero-vid">
                  <iframe
                    src="https://www.youtube.com/embed/eV7RRlNloZs"
                    title="Stemtrix Robotics Highlight Reel"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="stx-hero-badges">
                  {HERO_BADGES.map((b, i) => (
                    <div className="stx-badge" key={i}>
                      <div className="stx-badge-icon">{b.icon}</div>
                      <div>
                        <div className="stx-badge-title">{b.title}</div>
                        <div className="stx-badge-sub">{b.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            TRUST BAR
            ══════════════════════════════════════════════════════════════════ */}
        <section className="stx-trust" aria-labelledby="trust-heading">
          <div className="stx-container">
            <div className="stx-trust-header">
              <span className="stx-label">Proven Track Record</span>
              <h2 className="stx-section-title" id="trust-heading">
                <span className="black">Trusted by</span> Kenya's Top Schools
              </h2>
              <p className="stx-section-sub" style={{ margin: '0 auto' }}>
                From Nairobi to national competitions — hundreds of students and
                dozens of schools have already chosen Stemtrix.
              </p>
            </div>

            <div className="stx-trust-grid" role="list">
              {TRUST_STATS.map((s, i) => (
                <div className="stx-trust-cell" key={i} role="listitem">
                  <span className="stx-trust-num">{s.num}</span>
                  <div className="stx-trust-lbl">{s.lbl}</div>
                </div>
              ))}
            </div>

            <div className="stx-partners" aria-label="Partner schools">
              {PARTNERS.map((p, i) => (
                <div className="stx-partner-chip" key={i}>{p}</div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            PROGRAMS
            ══════════════════════════════════════════════════════════════════ */}
        <section className="stx-programs" aria-labelledby="programs-heading">
          <div className="stx-container">
            <div className="stx-programs-header">
              <span className="stx-label">What We Offer</span>
              <h2 className="stx-section-title" id="programs-heading">
                <span className="black">Programs Designed for</span> Every Learner
              </h2>
              <p className="stx-section-sub">
                From first-time builders to competition champions — a clear pathway
                for every student, school, and parent.
              </p>
            </div>

            <div className="stx-programs-grid">
              {PROGRAMS.map((p, i) => (
                <article className="stx-prog-card" key={i}>
                  <span className="stx-prog-emoji" aria-hidden="true">{p.emoji}</span>
                  <span className="stx-prog-tag">{p.tag}</span>
                  <h3 className="stx-prog-title">{p.title}</h3>
                  <p className="stx-prog-desc">{p.desc}</p>
                  <ul className="stx-prog-features" aria-label="Features">
                    {p.features.map((f, j) => (
                      <li key={j}>
                        <span className="stx-prog-check" aria-hidden="true">✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={p.href} className="stx-btn stx-btn-card">
                    {p.cta} <IconArrow />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            WHY STEMTRIX
            ══════════════════════════════════════════════════════════════════ */}
        <section className="stx-why" aria-labelledby="why-heading">
          <div className="stx-container">
            <div className="stx-why-inner">

              {/* Left */}
              <div className="stx-why-left">
                <span className="stx-label">Why Stemtrix</span>
                <h2 className="stx-section-title" id="why-heading">
                  <span className="black">We Don't Just</span> Teach Robotics.
                </h2>
                <p className="stx-section-sub">
                  We raise confident young innovators who can solve Africa's toughest
                  problems — and compete on the world stage.
                </p>

                <div className="stx-why-items">
                  {WHY_ITEMS.map((w, i) => (
                    <div className="stx-why-item" key={i}>
                      <div className="stx-why-icon" aria-hidden="true">{w.icon}</div>
                      <div>
                        <div className="stx-why-item-title">{w.title}</div>
                        <div className="stx-why-item-desc">{w.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right */}
              <div className="stx-why-right">
                <div className="stx-why-big" aria-label="500+ students trained">
                  <span className="stx-why-big-num">500+</span>
                  <div className="stx-why-big-lbl">young Kenyans trained and growing 🚀</div>
                </div>

                <div className="stx-why-mini-row">
                  {[
                    { icon: '🥇', lbl: 'National championship winners' },
                    { icon: '🌏', lbl: 'Teams sent to Beijing & Michigan' },
                    { icon: '📱', lbl: 'Weekly WhatsApp parent updates' },
                    { icon: '🎓', lbl: 'Certified completion awards' },
                  ].map((m, i) => (
                    <div className="stx-why-mini" key={i}>
                      <span className="stx-why-mini-icon" aria-hidden="true">{m.icon}</span>
                      <div className="stx-why-mini-lbl">{m.lbl}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            TESTIMONIALS
            ══════════════════════════════════════════════════════════════════ */}
        <section className="stx-testimonials" aria-labelledby="testi-heading">
          <div className="stx-container">
            <div className="stx-testimonials-header">
              <span className="stx-label">Real Stories, Real Results</span>
              <h2 className="stx-section-title" id="testi-heading">
                <span className="black">Hear From</span> Our Community
              </h2>
              <p className="stx-section-sub">
                Principals, parents, and coaches share what happened when Stemtrix
                walked through their doors.
              </p>
            </div>

            <div className="stx-testi-grid">
              {TESTIMONIALS.map((t, i) => (
                <figure className="stx-testi-card" key={i}>
                  <div className="stx-testi-stars" aria-label="5 stars">{t.stars}</div>
                  <blockquote className="stx-testi-quote">"{t.quote}"</blockquote>
                  <div className="stx-testi-result">{t.result}</div>
                  <figcaption className="stx-testi-author">
                    <div className="stx-testi-avatar" aria-hidden="true">{t.initials}</div>
                    <div>
                      <div className="stx-testi-name">{t.name}</div>
                      <div className="stx-testi-role">{t.role}</div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            NEWS
            ══════════════════════════════════════════════════════════════════ */}
        <section className="stx-news" aria-labelledby="news-heading">
          <div className="stx-container">
            <div className="stx-news-header">
              <div>
                <span className="stx-label">Latest Updates</span>
                <h2 className="stx-section-title" id="news-heading" style={{ marginBottom: 0 }}>
                  <span className="black">Stemtrix</span> in Action
                </h2>
              </div>
              <Link
                to="/news"
                className="stx-btn stx-btn-outline"
                style={{ padding: '10px 22px', fontSize: 13 }}
              >
                View All News <IconArrow />
              </Link>
            </div>

            <div className="stx-news-grid">
              {NEWS_ITEMS.map((n, i) => (
                <article className="stx-news-card" key={i}>
                  <iframe src={n.iframe} title={n.title} allowFullScreen loading="lazy" />
                  <div className="stx-news-body">
                    <div className="stx-news-date">{n.date}</div>
                    <h3 className="stx-news-title">{n.title}</h3>
                    <p className="stx-news-excerpt">{n.excerpt}</p>
                    <Link to={n.href} className="stx-news-link">
                      Read more <IconArrow />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            URGENCY CTA  (full red background section)
            ══════════════════════════════════════════════════════════════════ */}
        <section className="stx-cta" aria-labelledby="cta-heading">
          <div className="stx-container">
            <div className="stx-cta-inner">

              <div>
                <h2 className="stx-cta-h2" id="cta-heading">
                  Don't Let Your Child Miss the Robotics Revolution
                </h2>
                <p className="stx-cta-p">
                  Thousands of students across Kenya are already building robots,
                  winning competitions, and unlocking STEM careers. Enroll your child
                  today — or book a free demo for your school. August 2025 slots are limited.
                </p>
                <div className="stx-cta-btns">
                  <Link to="/join-us" className="stx-btn-white">
                    Enroll My Child Now <IconArrow />
                  </Link>
                  <Link to="/contact" className="stx-btn-white-outline">
                    Book a School Demo
                  </Link>
                </div>
              </div>

              <div className="stx-cta-cards">
                {CTA_CARDS.map((c, i) => (
                  <div className="stx-cta-card" key={i}>
                    <div className="stx-cta-card-icon" aria-hidden="true">{c.icon}</div>
                    <div>
                      <div className="stx-cta-card-title">{c.title}</div>
                      <div className="stx-cta-card-desc">{c.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            FLOATING WHATSAPP
            ══════════════════════════════════════════════════════════════════ */}
        <a
          href="https://wa.me/+254728128353?text=Hi%20Stemtrix!%20I%20visited%20your%20website%20and%20I%27m%20interested%20in%20enrolling%20my%20child%20in%20your%20robotics%20program.%20Please%20send%20me%20more%20details%20and%20upcoming%20dates."
          className={`stx-wa ${waVisible ? 'show' : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with Stemtrix on WhatsApp"
        >
          <IconWhatsApp />
          <span className="stx-wa-label">Chat with us</span>
        </a>

      </div>
    </>
  );
};

export default HomePage;